import { LOCALE } from '@foxrenderfarm/internals'
import { LocaleClass } from '../../utils'
import { ActionList, Anchor, EmailItem, FloatIcon, Wrapper } from './atoms'
import { ArrowIcon } from './svg/arrow-icon'
import { EmailIcon } from './svg/email-icon'
import { WhatsAppIcon } from './svg/whatsapp-icon'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMedia } from 'use-media'

const LEGACY_CHAT_PLUGIN_ID = 's-chat-plugin'
const CHAT_PLUGIN_SELECTOR = 'salesmartly-chat-widget'
// 给鼠标从聊天按钮移到白色操作模块预留缓冲，避免浮动栏闪烁。
const HIDE_ACTIONS_DELAY = 160

const getChatPlugin = () =>
  (document.querySelector(CHAT_PLUGIN_SELECTOR) ||
    document.getElementById(LEGACY_CHAT_PLUGIN_ID)) as HTMLElement | null

export const FloatBar = () => {
  const isMobile = useMedia({ maxWidth: 600 })
  const [email, setEmail] = useState('')
  const [showActions, setShowActions] = useState(false)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const { resEmail } = getEmailAndSkype(window.location.pathname)
    setEmail(resEmail)
  }, [])

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  const showFloatActions = useCallback(() => {
    clearHideTimer()
    setShowActions(true)
  }, [clearHideTimer])

  const hideFloatActions = useCallback(() => {
    clearHideTimer()
    hideTimerRef.current = setTimeout(() => {
      setShowActions(false)
      hideTimerRef.current = null
    }, HIDE_ACTIONS_DELAY)
  }, [clearHideTimer])

  const handleActionListMouseEnter = useCallback(() => {
    showFloatActions()
  }, [showFloatActions])

  const handleActionListMouseLeave = useCallback(() => {
    hideFloatActions()
  }, [hideFloatActions])

  useEffect(() => {
    if (isMobile) {
      setShowActions(false)
      return undefined
    }

    // Salesmartly 会在用户交互后注入聊天 iframe：
    // 如果已存在就立即绑定，否则使用 MutationObserver 持续观察 DOM，等它出现后再绑定。
    const bindChatPluginHover = () => {
      const chatPlugin = getChatPlugin()

      if (!chatPlugin) return undefined

      chatPlugin.addEventListener('mouseenter', showFloatActions)
      chatPlugin.addEventListener('mouseleave', hideFloatActions)

      return () => {
        chatPlugin.removeEventListener('mouseenter', showFloatActions)
        chatPlugin.removeEventListener('mouseleave', hideFloatActions)
      }
    }

    let unbindChatPluginHover = bindChatPluginHover()
    const observer = new MutationObserver(() => {
      if (unbindChatPluginHover) return
      unbindChatPluginHover = bindChatPluginHover()
    })

    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      unbindChatPluginHover?.()
      clearHideTimer()
    }
  }, [clearHideTimer, hideFloatActions, isMobile, showFloatActions])

  const getEmailAndSkype = (pathname: string) => {
    const currentLocale = LocaleClass.getCurrentLocaleName(pathname)
    const resEmail = LocaleClass.isEqualLocale(currentLocale, LOCALE.ES)
      ? 'quique@foxrenderfarm.com'
      : 'service@foxrenderfarm.com'
    return { resEmail }
  }

  const backToTop = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation()
    document.documentElement.scrollTo({ top: 0 })
  }

  return (
    <Wrapper>
      <ActionList
        $showActions={showActions}
        onMouseEnter={handleActionListMouseEnter}
        onMouseLeave={handleActionListMouseLeave}
      >
        <FloatIcon $hoverBgColor='#fff' $hoverSvgColor='#F3512B' onClick={backToTop}>
          <ArrowIcon />
        </FloatIcon>
        {!isMobile && (
          <>
            <FloatIcon $hideInMobile>
              <a
                href='https://api.whatsapp.com/send/?phone=8618124758259&text=Hello.May%20i%20help%20you?'
                target='_blank'
                rel='noopener noreferrer nofollow'
                aria-label='whatsapp'
              >
                <WhatsAppIcon />
              </a>
            </FloatIcon>
            <EmailItem $hideInMobile>
              <EmailIcon />
              <Anchor href={`mailto:${email}`}>E-mail: {email}</Anchor>
            </EmailItem>
          </>
        )}
      </ActionList>
    </Wrapper>
  )
}
